// import libs
import debounce from 'lodash/debounce'
import { select, selectAll } from 'd3-selection'
import { queryString, getHashParam, sendMessage, DOMReady } from './utils'
import './polyfills/Fetch';

// import chart frame
import ChartFrame from './chart-frame'

// import base styles'
import './app.scss'

// import json
import openingNotes from './opening-notes.json'
import issueNotes from './issue-notes.json'

// fix for IE
if ( typeof SVGElement.prototype.focus === 'undefined' ) {
    SVGElement.prototype.focus = function(){}
}

// enable hot module replacement
if ( module.hot ) {
	module.hot.accept()
}

// request params
const params = {
	action: 'sheet-data',
	list_id: '1y1-lhXkc_GGUTpIILukc6GmAp7-kfl2sNfOcBQcbcFY',
}

// config constants
const provList = ['bc', 'ab', 'sk', 'mb', 'on', 'qc', 'nb', 'ns', 'pei', 'nl', 'yt', 'nwt', 'nu', 'rc', 'ca']
const dataTypes = { cases: 'confirmed cases', deaths: 'deaths', recov: 'recoveries' }
const chartTypes = [ 'cumulative', 'histogram' ]
const logScale = false

// config initials
let maxValue
let prevValue
let testsValue
let updateValue
let provinceName = ''
let latest = ''

// config user settings
let province = queryString( 'prov' )
if ( ! province || ! provList.indexOf( province ) <= -1 ) {
	province = 'ca'
}

let dataType = queryString( 'data' )
if ( ! dataType || ! Object.keys( dataTypes ).indexOf( dataType ) <= -1 ) {
	dataType = 'cases'
}

let chartType = queryString( 'chart' )
if ( ! chartType || ! chartTypes.indexOf( chartType ) <= -1 ) {
	chartType = 'histogram'
}

const showTests = 'cases' === dataType
const untitled = queryString( 'untitled' )

DOMReady( () => {
	const $interactive = document.querySelector( '.l-interactive' )

	if ( untitled ) {
		$interactive.classList.add( 'l-interactive--untitled' )
	}

	if ( 'histogram' === chartType ) {
		const $navItems = $interactive.querySelectorAll( '.l-interactive__navItem' );
		[].forEach.call( $navItems, ( $elem ) => {
			$elem.classList.add( 'l-interactive__navItem--inactive' )
		})

		const $currentNav = $interactive.querySelector( `.l-interactive__navItem[data-chart-type="${chartType}"]` )
		if ( $currentNav ) {
			$currentNav.classList.remove( 'l-interactive__navItem--inactive' )
		}
	}

	// Populate with data
	fetch( `https://globalnews.ca/gnca-ajax-redesign/${params.action}/${encodeURI( JSON.stringify( params ) )}` )
		.then( response => response.text() )
		.then( ( content ) => {
			const data = JSON.parse( content )

			if ( data.feed && data.feed.entry && data.feed.entry.length > 2 ) {
				let dailyStats = []

				// Grab province label
				if ( data.feed.entry[0][`gsx$${province}-cases`] ) {
					provinceName = data.feed.entry[0][`gsx$${province}-cases`].$t
				}

				// Parse data for specified province
				data.feed.entry.slice(2).forEach( ( row ) => {
					let rowData = []

					Object.keys( row ).forEach( ( key ) => {
						if ( key.indexOf( 'gsx$date' ) > -1
							|| key.indexOf( `gsx$${province}-` ) > -1 ) {
							let newKey = key.replace('gsx$', '')
							newKey = newKey.replace(`${province}-`, '')
							rowData[ newKey ] = row[ key ].$t
						}
					})

					dailyStats.push( rowData )
				})

				// Drop last row if it contains no new data or if it's national data
				latest = 'today'
				if ( dailyStats[ dailyStats.length - 1 ][ dataType ] === dailyStats[ dailyStats.length - 2 ][ dataType ] || 'ca' === province ) {
					dailyStats.pop()
					latest = 'yesterday'
				}

				// Remove rows with NaN values
				while( isNaN( dailyStats[ dailyStats.length - 1][ dataType ] ) ) {
					dailyStats.pop()
				}

				// Get max value and tests value
				maxValue = parseInt( dailyStats[ dailyStats.length - 1 ][ dataType ], 10 )
				prevValue = parseInt( dailyStats[ dailyStats.length - 2 ][ dataType ], 10 )
				testsValue = parseInt( dailyStats[ dailyStats.length - 1 ]['tests'], 10 )
				updateValue = maxValue - prevValue

				// Get reopen date if it's set and update footnote
				const reopening = getReopenDate( data.feed.entry, province )
				if ( reopening ) {
					addReopeningNote( $interactive, province )
				}

				// Note any issues associated with the data
				addIssueNotes( $interactive, province )

				// Set chart title
				setTitle({
					provinceName,
					dataType,
					chartType,
					maxValue
				})

				// Set summary stats
				if ( showTests && 'Canada' !== provinceName ) {
					setSummary({
						entries: data.feed.entry,
						provinceName,
						maxValue
					})
				}

				// Get min y-axis bound
				let minY = 0
				if ( data.feed.entry[3] && data.feed.entry[3]['gsx$bounds'] ) {
					minY = data.feed.entry[3]['gsx$bounds'].$t
					minY = parseInt( minY, 10 )
				}

				// Initialize chart frame
				ChartFrame.init({
					data: dailyStats, 
					chartType: chartType,
					dataType: dataType,
					typeFormatted: dataTypes[ dataType ],
					yAxisLimit: minY,
					showTests: showTests,
					logScale: logScale,
					highlights: [{
						'label1': 'Reopening',
						'label2': 'Begins*',
						'date': reopening
					}]
				})
				
				// Populate Last Updated
				if ( data.feed.entry[2] && data.feed.entry[2]['gsx$lastupdated'] ) {
					const updated = data.feed.entry[2]['gsx$lastupdated'].$t
					const $timeStamp = document.querySelector( '.l-interactive__updated time' )
					$timeStamp.innerText = `${updated} EDT`
				}

				// Add chart type listener
				const $nav = document.querySelectorAll( '.l-interactive__nav' );
				[].forEach.call( $nav, ( $navItem ) => {
					$navItem.addEventListener( 'click', toggleChart )
				})
			}

			// Resize after content loaded
			sendMessage({
				'height': document.documentElement.offsetHeight,
				'action': 'setHeight'
			})
		})

	// Initial sizing
	sendMessage({
		'height': document.documentElement.offsetHeight,
		'action': 'setHeight'
	})

	// bind event to capture resize and orientation change
	const resizeHandler = debounce( () => {
		ChartFrame.render()
		sendMessage({
			'height': document.documentElement.offsetHeight,
			'action': 'setHeight'
		})
	}, 200 )
	window.addEventListener( 'orientationchange', resizeHandler )
	window.addEventListener( 'resize', resizeHandler )
})

function setTitle( config ) {
	// Set province Name
	document.querySelector( '.l-interactive__locale' ).innerText = config.provinceName

	// Set interactive type
	document.querySelector( '.l-interactive' ).classList.add( `l-interactive--${dataType}`)

	if ( 'cumulative' === chartType ) {
		// Cumulative chart title
		let template = `
			<span class="l-interactive__figure">${config.maxValue.toLocaleString()}</span>
			<span class="l-interactive__class"> total cumulative </span>
			<span class="l-interactive__figure">${dataTypes[dataType]}</span>`

		document.querySelector( '.l-interactive__title' ).innerHTML = template
	} else {
		// Histogram title
		let template = `
			<span class="l-interactive__figure">New ${dataTypes[dataType]}</span>
			<span class="l-interactive__frequency"> by day</span>`

		if ( ! isNaN( updateValue ) ) {
			template += `<span class="l-interactive__update"> (+${updateValue.toLocaleString()} ${latest})</span>`
		}

		document.querySelector( '.l-interactive__title' ).innerHTML = template
	}
}

function setSummary( config ) {
	// parse population values
	let provincePop = false
	let canadaPop = false
	const firstRow = config.entries[0]
	Object.keys( firstRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-recov` ) > -1 ) {
			provincePop = parseInt( firstRow[ key ].$t, 10 )
		}
	})

	// parse case totals
	let provinceCases = false
	const lastRow = config.entries[ config.entries.length - 1 ]
	Object.keys( lastRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-cases` ) > -1 ) {
			provinceCases = parseInt( lastRow[ key ].$t, 10 )
		}
	})

	// bail if bad data
	if ( isNaN( provincePop )
		|| isNaN( provinceCases ) ) {
		return;
	}

	// calculate ratios
	const provCase100k = ( provinceCases * 100000 ) / provincePop

	// add template
	const template = `
		<span>${config.provinceName} has </span>
		<span class="l-interactive__figure">${provCase100k.toFixed(0)} confirmed cases per 100k</span>
		<span> residents.</span>`

	document.querySelector( '.l-interactive__summary' ).innerHTML = template
}

function toggleChart( $navItem ) {
	if ( $navItem.target.dataset.chartType ) {
		chartType = $navItem.target.dataset.chartType
		ChartFrame.updateChartType( chartType )
		ChartFrame.render()
	}

	[].forEach.call( $navItem.target.parentElement.children, ( $elem ) => {
		$elem.classList.add( 'l-interactive__navItem--inactive' )
	})

	$navItem.target.classList.remove( 'l-interactive__navItem--inactive' )

	setTitle({
		provinceName,
		dataType,
		chartType,
		maxValue
	})
}

function getReopenDate( entries, province ) {
	let reopening = false
	const firstRow = entries[0]

	Object.keys( firstRow ).forEach( ( key ) => {
		if ( key.indexOf( `gsx$${province}-tests` ) > -1 && firstRow[ key ].$t.length > 0 ) {
			let newDate = new Date( firstRow[ key ].$t )
			if ( newDate instanceof Date && ! isNaN( newDate ) ) {
				reopening = newDate
			}
		}
	})

	return reopening
}

function addReopeningNote( $interactive, province ) {
	if ( openingNotes && openingNotes[ province ] ) {
		const $footnote = $interactive.querySelector( '.l-interactive__footnote' )

		if ( $footnote ) {
			$footnote.innerText = `* ${openingNotes[ province ]}`
			$footnote.setAttribute( 'style', 'margin-top: 12px;' )
		}
	}
}

function addIssueNotes( $interactive, province ) {
	if ( issueNotes && issueNotes[ province ] ) {
		const $issuenote = $interactive.querySelector( '.l-interactive__issues' )

		if ( $issuenote ) {
			$issuenote.innerText = `${issueNotes[ province ]}`
			$issuenote.setAttribute( 'style', 'margin-top: 12px;' )
		}
	}
}
